import { useEffect, useRef, useState } from "react";

/**
 * Observes a container element and returns its current height in pixels.
 * Uses ResizeObserver for live updates when the container resizes.
 *
 * @param offset - Pixels to subtract from the measured height (e.g. for indicators / padding)
 * @returns [ref, height] — attach the ref to the container element
 */
export const useContainerHeight = <T extends HTMLElement = HTMLDivElement>(
    offset = 0,
): [React.RefObject<T | null>, number | undefined] => {
    const ref = useRef<T | null>(null);
    const [height, setHeight] = useState<number | undefined>(undefined);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const measured = entry.contentRect.height;
                setHeight(Math.max(0, Math.floor(measured - offset)));
            }
        });

        observer.observe(element);
        return () => observer.disconnect();
    }, [offset]);

    return [ref, height];
};
